import { FormEvent, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  BsArrowCounterclockwise,
  BsPencilSquare,
  BsPlusCircle,
  BsTrash3,
} from "react-icons/bs";
import { DTOEquipoInventario } from "../../../DTO/Inventario/DTOEquipoInventario";
import {
  DTOCatalogosRespaldos,
  DTOGestionarRespaldo,
  DTORespaldoInventario,
} from "../../../DTO/Inventario/DTORespaldos";
import RespaldosService from "../../../Services/Catalogos/RespaldosService";
import { MsgInformativo } from "../../../Utils/Toast/msgInformativo";
import "./Respaldos.css";

interface Props {
  show: boolean;
  equipo: DTOEquipoInventario | null;
  onCerrar: () => void;
}

interface FormularioRespaldo {
  nombreRespaldo: string;
  codigoFrecuencia: string;
  horaProgramada: string;
  diaSemana: string;
  diaMes: string;
  mesAnio: string;
  contenido: string;
  rutaRespaldo: string;
  notas: string;
}

const formularioVacio = (): FormularioRespaldo => ({
  nombreRespaldo: "",
  codigoFrecuencia: "DIARIO",
  horaProgramada: "00:00",
  diaSemana: "1",
  diaMes: "1",
  mesAnio: "1",
  contenido: "",
  rutaRespaldo: "",
  notas: "",
});

const obtenerMensajeError = (error: unknown) => {
  const e = error as { response?: { data?: { mensaje?: string } }; message?: string };
  return e.response?.data?.mensaje || e.message || "Ocurrió un error inesperado.";
};

const ConfiguracionRespaldos = ({ show, equipo, onCerrar }: Props) => {
  const [respaldos, setRespaldos] = useState<DTORespaldoInventario[]>([]);
  const [catalogos, setCatalogos] = useState<DTOCatalogosRespaldos>({
    frecuencias: [],
    estados: [],
  });
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [seleccionado, setSeleccionado] = useState<DTORespaldoInventario | null>(null);
  const [formulario, setFormulario] = useState<FormularioRespaldo>(formularioVacio());
  const [showMsg, setShowMsg] = useState(false);
  const [msg, setMsg] = useState("");
  const [header, setHeader] = useState("");
  const [msgColor, setMsgColor] = useState("primary");

  const mostrarMensaje = (titulo: string, mensaje: string, color: string) => {
    setHeader(titulo);
    setMsg(mensaje);
    setMsgColor(color);
    setShowMsg(true);
  };

  const cargar = async () => {
    if (!equipo) return;
    setCargando(true);
    try {
      const [respuestaRespaldos, respuestaCatalogos] = await Promise.all([
        RespaldosService.obtenerPorEquipo(equipo.idEquipo, mostrarInactivos),
        RespaldosService.obtenerCatalogos(),
      ]);
      setRespaldos(respuestaRespaldos.data || []);
      setCatalogos(respuestaCatalogos.data);
    } catch (error) {
      mostrarMensaje("Error", obtenerMensajeError(error), "danger");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (show && equipo) void cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, equipo?.idEquipo, mostrarInactivos]);

  const abrirNuevo = () => {
    setSeleccionado(null);
    setFormulario(formularioVacio());
    setMostrarFormulario(true);
  };

  const abrirEditar = (respaldo: DTORespaldoInventario) => {
    setSeleccionado(respaldo);
    setFormulario({
      nombreRespaldo: respaldo.nombreRespaldo,
      codigoFrecuencia: respaldo.codigoFrecuencia,
      horaProgramada: respaldo.horaProgramada.substring(0, 5),
      diaSemana: String(respaldo.diaSemana || 1),
      diaMes: String(respaldo.diaMes || 1),
      mesAnio: String(respaldo.mesAnio || 1),
      contenido: respaldo.contenido || "",
      rutaRespaldo: respaldo.rutaRespaldo || "",
      notas: respaldo.notas || "",
    });
    setMostrarFormulario(true);
  };

  const cambiar = (campo: keyof FormularioRespaldo, valor: string) => {
    setFormulario((actual) => ({ ...actual, [campo]: valor }));
  };

  const guardar = async (event: FormEvent) => {
    event.preventDefault();
    if (!equipo || !formulario.nombreRespaldo.trim() || !formulario.horaProgramada) {
      mostrarMensaje("Informativo", "Nombre y hora son obligatorios.", "warning");
      return;
    }

    const frecuencia = formulario.codigoFrecuencia;
    const dto: DTOGestionarRespaldo = {
      accion: seleccionado ? "ACTUALIZAR" : "INSERTAR",
      idRespaldo: seleccionado?.idRespaldo,
      idEquipo: equipo.idEquipo,
      nombreRespaldo: formulario.nombreRespaldo.trim(),
      codigoFrecuencia: frecuencia,
      horaProgramada: `${formulario.horaProgramada}:00`,
      diaSemana: frecuencia === "SEMANAL" ? Number(formulario.diaSemana) : null,
      diaMes:
        frecuencia === "MENSUAL" || frecuencia === "ANUAL"
          ? Number(formulario.diaMes)
          : null,
      mesAnio: frecuencia === "ANUAL" ? Number(formulario.mesAnio) : null,
      contenido: formulario.contenido.trim() || null,
      rutaRespaldo: formulario.rutaRespaldo.trim() || null,
      notas: formulario.notas.trim() || null,
    };

    setGuardando(true);
    try {
      const respuesta = await RespaldosService.gestionar(dto);
      mostrarMensaje("Éxito", respuesta.data.mensaje, "success");
      setMostrarFormulario(false);
      await cargar();
    } catch (error) {
      mostrarMensaje("Error", obtenerMensajeError(error), "danger");
    } finally {
      setGuardando(false);
    }
  };

  const cambiarActivo = async (respaldo: DTORespaldoInventario) => {
    const accion = respaldo.activo ? "ELIMINAR" : "REACTIVAR";
    if (
      respaldo.activo &&
      !window.confirm(`¿Deseas desactivar el respaldo "${respaldo.nombreRespaldo}"?`)
    ) return;

    try {
      const respuesta = await RespaldosService.gestionar({
        accion,
        idRespaldo: respaldo.idRespaldo,
      });
      mostrarMensaje("Éxito", respuesta.data.mensaje, "success");
      await cargar();
    } catch (error) {
      mostrarMensaje("Error", obtenerMensajeError(error), "danger");
    }
  };

  const descripcionProgramacion = (respaldo: DTORespaldoInventario) => {
    if (respaldo.codigoFrecuencia === "SEMANAL")
      return `${respaldo.nombreDiaSemana} ${respaldo.horaProgramada.substring(0, 5)}`;
    if (respaldo.codigoFrecuencia === "MENSUAL")
      return `Día ${respaldo.diaMes} · ${respaldo.horaProgramada.substring(0, 5)}`;
    if (respaldo.codigoFrecuencia === "ANUAL")
      return `${respaldo.diaMes}/${respaldo.mesAnio} · ${respaldo.horaProgramada.substring(0, 5)}`;
    return `Todos los días · ${respaldo.horaProgramada.substring(0, 5)}`;
  };

  return (
    <>
      <Modal show={show} onHide={onCerrar} size="xl" centered fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>Respaldos de {equipo?.nombreEquipo}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="configuracion-respaldos">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <Form.Check
              type="switch"
              label="Incluir respaldos inactivos"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
            />
            <Button onClick={abrirNuevo} disabled={!equipo?.activo}>
              <BsPlusCircle className="me-2" />Agregar respaldo
            </Button>
          </div>

          {cargando ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : respaldos.length === 0 ? (
            <div className="respaldo-vacio">Este equipo todavía no tiene respaldos configurados.</div>
          ) : (
            <>
              <div className="tabla-respaldos-escritorio">
                <Table responsive hover className="align-middle">
                  <thead><tr><th>Respaldo</th><th>Programación</th><th>Contenido</th><th>Ruta</th><th>Estado</th><th /></tr></thead>
                  <tbody>{respaldos.map((respaldo) => (
                    <tr key={respaldo.idRespaldo}>
                      <td className="fw-semibold">{respaldo.nombreRespaldo}</td>
                      <td><Badge bg="primary">{respaldo.frecuencia}</Badge><div className="small mt-1">{descripcionProgramacion(respaldo)}</div></td>
                      <td>{respaldo.contenido || "—"}</td>
                      <td className="font-monospace small">{respaldo.rutaRespaldo || "—"}</td>
                      <td><Badge bg={respaldo.activo ? "success" : "secondary"}>{respaldo.activo ? "Activo" : "Inactivo"}</Badge></td>
                      <td><div className="d-flex gap-1 justify-content-end">
                        {respaldo.activo && <Button size="sm" variant="outline-secondary" onClick={() => abrirEditar(respaldo)}><BsPencilSquare /></Button>}
                        <Button size="sm" variant={respaldo.activo ? "outline-danger" : "outline-success"} onClick={() => cambiarActivo(respaldo)}>
                          {respaldo.activo ? <BsTrash3 /> : <BsArrowCounterclockwise />}
                        </Button>
                      </div></td>
                    </tr>
                  ))}</tbody>
                </Table>
              </div>
              <div className="lista-respaldos-movil">{respaldos.map((respaldo) => (
                <div className="respaldo-config-card" key={respaldo.idRespaldo}>
                  <div className="d-flex justify-content-between gap-2"><strong>{respaldo.nombreRespaldo}</strong><Badge bg={respaldo.activo ? "success" : "secondary"}>{respaldo.activo ? "Activo" : "Inactivo"}</Badge></div>
                  <div className="text-muted small mt-2">{descripcionProgramacion(respaldo)}</div>
                  <div className="small mt-2">{respaldo.contenido || "Sin descripción"}</div>
                  <div className="font-monospace small text-break mt-1">{respaldo.rutaRespaldo || "Sin ruta"}</div>
                  <div className="d-flex gap-2 mt-3">
                    {respaldo.activo && <Button size="sm" variant="outline-secondary" onClick={() => abrirEditar(respaldo)}><BsPencilSquare className="me-1" />Editar</Button>}
                    <Button size="sm" variant={respaldo.activo ? "outline-danger" : "outline-success"} onClick={() => cambiarActivo(respaldo)}>{respaldo.activo ? "Desactivar" : "Reactivar"}</Button>
                  </div>
                </div>
              ))}</div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={onCerrar}>Cerrar</Button></Modal.Footer>
      </Modal>

      <Modal show={mostrarFormulario} onHide={() => !guardando && setMostrarFormulario(false)} size="lg" centered fullscreen="sm-down">
        <Form onSubmit={guardar}>
          <Modal.Header closeButton><Modal.Title>{seleccionado ? "Editar respaldo" : "Agregar respaldo"}</Modal.Title></Modal.Header>
          <Modal.Body><Row className="g-3">
            <Col md={8}><Form.Label>Nombre *</Form.Label><Form.Control required maxLength={200} value={formulario.nombreRespaldo} onChange={(e) => cambiar("nombreRespaldo", e.target.value)} /></Col>
            <Col md={4}><Form.Label>Hora *</Form.Label><Form.Control required type="time" value={formulario.horaProgramada} onChange={(e) => cambiar("horaProgramada", e.target.value)} /></Col>
            <Col md={4}><Form.Label>Frecuencia *</Form.Label><Form.Select value={formulario.codigoFrecuencia} onChange={(e) => cambiar("codigoFrecuencia", e.target.value)}>{catalogos.frecuencias.map((x) => <option key={x.codigo} value={x.codigo}>{x.frecuencia}</option>)}</Form.Select></Col>
            {formulario.codigoFrecuencia === "SEMANAL" && <Col md={4}><Form.Label>Día de la semana</Form.Label><Form.Select value={formulario.diaSemana} onChange={(e) => cambiar("diaSemana", e.target.value)}>{["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"].map((x, i) => <option key={x} value={i + 1}>{x}</option>)}</Form.Select></Col>}
            {(formulario.codigoFrecuencia === "MENSUAL" || formulario.codigoFrecuencia === "ANUAL") && <Col md={4}><Form.Label>Día del mes</Form.Label><Form.Control type="number" min={1} max={31} value={formulario.diaMes} onChange={(e) => cambiar("diaMes", e.target.value)} /></Col>}
            {formulario.codigoFrecuencia === "ANUAL" && <Col md={4}><Form.Label>Mes</Form.Label><Form.Select value={formulario.mesAnio} onChange={(e) => cambiar("mesAnio", e.target.value)}>{["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map((x, i) => <option key={x} value={i + 1}>{x}</option>)}</Form.Select></Col>}
            <Col md={12}><Form.Label>Contenido</Form.Label><Form.Control maxLength={500} value={formulario.contenido} onChange={(e) => cambiar("contenido", e.target.value)} /></Col>
            <Col md={12}><Form.Label>Ruta</Form.Label><Form.Control className="font-monospace" maxLength={500} value={formulario.rutaRespaldo} onChange={(e) => cambiar("rutaRespaldo", e.target.value)} /></Col>
            <Col md={12}><Form.Label>Notas</Form.Label><Form.Control as="textarea" rows={3} maxLength={1000} value={formulario.notas} onChange={(e) => cambiar("notas", e.target.value)} /></Col>
          </Row></Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setMostrarFormulario(false)} disabled={guardando}>Cancelar</Button><Button type="submit" disabled={guardando}>{guardando ? <><Spinner size="sm" className="me-2" />Guardando...</> : "Guardar"}</Button></Modal.Footer>
        </Form>
      </Modal>

      <MsgInformativo show={showMsg} header={header} msg={msg} msgColor={msgColor} time={3500} closeToast={() => setShowMsg(false)} />
    </>
  );
};

export default ConfiguracionRespaldos;
